import { FC } from 'react';
import { TabbedFormProps, useNotify, useRecordContext, useRefresh, useUpdate } from 'react-admin';

import CraftEdit from 'src/components/CraftEdit';
import CraftTabbedForm from 'src/components/CraftTabbedForm';
import LoadingOverlay from 'src/components/LoadingOverlay';
import ArchivedOrDeletedNotification from 'src/components/ArchivedOrDeletedNotification';
import DetailsTab from './formTabs/Details';
import EditAside from './EditAside';
import { ApiFieldRecord } from 'src/types';
import { apiFieldSchema, getFormValidator } from './validation';

const validateForm = getFormValidator();

const transformValues = (rawValues: Partial<ApiFieldRecord>) => {
  const values = apiFieldSchema.parse(rawValues);
  return {
    ...values,
    updated_at: 'now()',
  };
};

const CustomTabbedForm = ({ children, ...rest }: TabbedFormProps) => {
  const record = useRecordContext<ApiFieldRecord>();
  const notify = useNotify();
  const refresh = useRefresh();

  const [softDeleteApiField, { isLoading: softDeleteApiFieldLoading }] = useUpdate();

  const handleDelete = async () => {
    if (!record) return;

    try {
      await softDeleteApiField('api_fields', { id: record.id, data: { deleted_at: 'now()' } }, { returnPromise: true });
      notify('The API Field has been successfully deleted', { type: 'success' });
      refresh();
    } catch (e) {
      console.error(e);
      notify('There was a problem deleting the API Field. Please try again.', { type: 'error' });
    }
  };

  return (
    <>
      <CraftTabbedForm
        formType="edit"
        deleteOptions={{
          deletePermission: 'apiField:delete',
          onConfirmDelete: handleDelete,
          dialogTitle: `Delete API Field "${record?.path}"`,
        }}
        validate={validateForm}
        shouldUnregister
        {...rest}
      >
        {children}
      </CraftTabbedForm>

      <LoadingOverlay open={softDeleteApiFieldLoading} />
      <ArchivedOrDeletedNotification record={record} notificationText="This API Field is DELETED" />
    </>
  );
};

const ApiFieldsEdit: FC = () => (
  <CraftEdit
    header="Edit API Field"
    aside={<EditAside />}
    redirect={false}
    transform={transformValues}
    mutationMode="optimistic"
  >
    <CustomTabbedForm>
      <DetailsTab mode="edit" />
    </CustomTabbedForm>
  </CraftEdit>
);

export default ApiFieldsEdit;
